import { useCallback, useEffect, useId, useState } from "react";
import { Button, Icon, Notice, TextField } from "@conuti-das/prince-ui";
import { Flex, Spinner, Stack, princeIcon } from "@/ui-prince";
import { changelogApi, type ChangelogComment } from "@/lib/changelogApi";
import { useIdentity } from "@/hooks/useIdentity";
import { useRunnerHealth } from "@/hooks/useRunnerHealth";
import "./changelogDialog.css";

type Props = {
  runId: string;
  entryKey: string;
  /** Meldet ungespeicherten Kommentartext. */
  onDirtyChange?: (dirty: boolean) => void;
};

function message(exc: unknown): string {
  return String((exc as Error)?.message ?? exc);
}

const DATE_FMT = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" });

/** Geteilte Kommentare zur inhaltlichen Bewertung einer Änderung (Runner-DB). */
export function CommentsSection({ runId, entryKey, onDirtyChange }: Props) {
  const identity = useIdentity();
  const { online } = useRunnerHealth();
  const ids = useId();
  const [comments, setComments] = useState<ChangelogComment[] | null>(null);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [authorDraft, setAuthorDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoadError("");
    changelogApi
      .listComments(runId, entryKey)
      .then((res) => setComments(res.comments))
      .catch((exc) => setLoadError(message(exc)));
  }, [runId, entryKey]);

  useEffect(load, [load]);

  const dirty = text.trim() !== "";
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const author = (identity.hasIdentity ? identity.name ?? "" : authorDraft).trim();

  const save = () => {
    setSaving(true);
    setError("");
    changelogApi
      .addComment(runId, entryKey, { author, text: text.trim() })
      .then((c) => {
        setComments((prev) => [...(prev ?? []), c]);
        setText("");
        if (!identity.hasIdentity) identity.setName(author);
      })
      .catch((exc) => setError(message(exc)))
      .finally(() => setSaving(false));
  };

  const remove = (c: ChangelogComment) => {
    setConfirmDelete(null);
    setError("");
    changelogApi
      .deleteComment(runId, entryKey, c.id)
      .then(() => setComments((prev) => (prev ?? []).filter((x) => x.id !== c.id)))
      .catch((exc) => setError(message(exc)));
  };

  if (comments === null && loadError) {
    return (
      <Stack className="cl-v2-stack">
        <Notice tone="critical">Kommentare nicht verfügbar: {loadError}</Notice>
        <Flex justifyContent="Start">
          <Button variant="plain" onPress={load}>Erneut laden</Button>
        </Flex>
      </Stack>
    );
  }
  if (comments === null) {
    return (
      <Flex alignItems="Center" className="cl-v2-busy">
        <Spinner />
        <span className="muted">Kommentare werden geladen …</span>
      </Flex>
    );
  }

  return (
    <Stack className="cl-v2-stack">
      {!online && (
        <Notice tone="critical">
          Runner nicht erreichbar — Kommentare nur lesbar, bis er wieder antwortet.
        </Notice>
      )}
      {error && <Notice tone="negative">{error}</Notice>}
      {comments.length === 0 && <span className="muted">Noch keine Kommentare.</span>}
      {comments.map((c) => (
        <div key={c.id} className="cl-v2-comment">
          <Flex justifyContent="SpaceBetween" alignItems="Baseline">
            <span className="cl-v2-comment-meta">
              {c.author} · {DATE_FMT.format(new Date(c.created_at))}
            </span>
            <Button
              variant="plain"
              size="s"
              aria-label="Kommentar löschen"
              isDisabled={!online}
              onPress={() => setConfirmDelete(c.id)}
            >
              <Icon name={princeIcon("delete")} size={14} />
            </Button>
          </Flex>
          <div className="cl-v2-comment-text">{c.text}</div>
          {confirmDelete === c.id && (
            <Flex className="cl-v2-actions" alignItems="Center" justifyContent="End">
              <span className="muted">Kommentar von {c.author} löschen? Er ist für alle weg.</span>
              <Button variant="plain" size="s" onPress={() => setConfirmDelete(null)}>Abbrechen</Button>
              <Button variant="tinted" size="s" isDisabled={!online} onPress={() => remove(c)}>Löschen</Button>
            </Flex>
          )}
        </div>
      ))}

      {identity.hasIdentity ? (
        <span className="muted cl-v2-comment-meta">Kommentieren als {identity.name}</span>
      ) : (
        <TextField label="Dein Name" value={authorDraft} onChange={setAuthorDraft} className="prn-fill" />
      )}
      <div>
        <label className="prn-field-label" htmlFor={`${ids}-text`}>Neuer Kommentar</label>
        <textarea
          id={`${ids}-text`}
          className="prn-input"
          value={text}
          rows={3}
          disabled={!online}
          placeholder="Inhaltliche Bewertung der Änderung …"
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      <Flex className="cl-v2-actions" justifyContent="End">
        <Button variant="filled" isDisabled={!online || !dirty || !author || saving} onPress={save}>
          Kommentar speichern
        </Button>
      </Flex>
    </Stack>
  );
}
